
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '@/types/lead';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Check, Pencil, Trash2, User, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { TaskWithLead } from '@/types/taskFilters';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface TaskCardProps {
  task: TaskWithLead;
  onEdit: (task: TaskWithLead) => void;
  onDelete: (taskId: string) => void;
  onComplete: (taskId: string) => void;
  getPriorityBadge: (priority: string) => React.ReactNode;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onEdit,
  onDelete,
  onComplete,
  getPriorityBadge
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: task.id,
    data: {
      task
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCompleted = task.status === 'completed';
  const formattedDate = task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : '';
  
  const renderCategoryBadges = () => {
    if (!task.categories || task.categories.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {task.categories.slice(0, 1).map(category => (
          <Badge key={category} variant="outline" className="flex items-center gap-1 text-xs bg-slate-100">
            <Tag className="h-3 w-3" />
            {category}
          </Badge>
        ))}
        {task.categories.length > 1 && (
          <Badge variant="outline" className="flex items-center gap-1 text-xs bg-slate-100">
            +{task.categories.length - 1}
          </Badge>
        )}
      </div>
    );
  };
  
  return (
    <div 
      ref={setNodeRef} 
      style={style}
      {...attributes} 
      {...listeners}
      className={`p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow relative group ${isDragging ? 'z-50 shadow-lg' : ''}`}
    >
      <div className={`flex flex-col ${isCompleted ? 'opacity-60' : ''}`}>
        <div className="flex justify-between items-start mb-2">
          <h4 className={`font-medium text-sm ${isCompleted ? 'line-through' : ''}`}>
            {task.title}
          </h4>
          <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!isCompleted && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete(task.id);
                  }}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {task.description && (
          <p className={`text-xs text-muted-foreground mb-2 ${isCompleted ? 'line-through' : ''}`}>
            {task.description}
          </p>
        )}
        
        <div className="flex flex-col space-y-2">
          <div className="flex items-center text-xs text-muted-foreground">
            <Badge variant="outline" className="mr-2 text-xs">
              {task.leads.business_name}
            </Badge>
          </div>
          
          {renderCategoryBadges()}
          
          <div className="flex flex-wrap gap-1.5">
            {getPriorityBadge(task.priority)}
            
            {task.dueDate && (
              <Badge variant="outline" className="flex items-center gap-1 text-xs">
                <Calendar className="h-3 w-3" />
                {formattedDate}
              </Badge>
            )}

            {task.assignee ? (
              <div className="flex items-center gap-1 text-xs">
                <Avatar className="h-4 w-4">
                  <AvatarImage src={task.assignee.avatar_url || ''} />
                  <AvatarFallback className="text-[8px]">
                    {task.assignee.first_name[0]}{task.assignee.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                  {task.assignee.first_name} {task.assignee.last_name}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span>Unassigned</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
